import { memo } from 'react'

import { heightPctAt, scaleAt, type DepthLike } from '@/geo/depth'
import { ASPECT, VIEW_W } from '@/geo/types'

/**
 * The horizon and its ghost silhouette (§8.2, §9.6).
 *
 * The number `depth.horizon_y` is meaningless to a teacher. The silhouette
 * is not: it draws a real child-sized figure at three depths using exactly
 * the player's `scaleAt`, so "drag until the fox looks the right size" is a
 * literal instruction rather than a metaphor.
 */

export interface HorizonLineProps {
  depth: DepthLike
  /** Real height of the reference character, cm. 110 = a 4-year-old. */
  heightCm?: number
  /** Rows the ghosts stand on, percent-y. */
  sampleRows?: number[]
  active?: boolean
  onPointerDown?: (ev: React.PointerEvent<SVGElement>) => void
}

const GHOST_ROWS = [62, 78, 94]

export const HorizonLine = memo(function HorizonLine({ depth, heightCm = 110, sampleRows = GHOST_ROWS, active, onPointerDown }: HorizonLineProps) {
  const horizonY = depth.mode === 'horizon' ? (depth.horizon_y ?? 42) : undefined
  if (horizonY === undefined) return null
  const y = horizonY * ASPECT

  return (
    <g className="horizon">
      {sampleRows.map((row) => {
        if (row <= horizonY) return null
        const h = heightPctAt(heightCm, row, depth) * ASPECT
        const w = h * 0.34
        const feetY = row * ASPECT
        const x = 8 + (VIEW_W - 16) * (sampleRows.indexOf(row) / Math.max(1, sampleRows.length - 1)) * 0.86
        return (
          <g key={row} opacity={0.55} pointerEvents="none">
            {/* head + body, drawn from the feet up so the pivot matches the player */}
            <ellipse cx={x} cy={feetY - h * 0.85} rx={w * 0.34} ry={h * 0.15} fill="var(--color-ink, black)" opacity={0.7} />
            <path
              d={`M${x - w / 2},${feetY} L${x - w * 0.28},${feetY - h * 0.7} Q${x},${feetY - h * 0.78} ${x + w * 0.28},${feetY - h * 0.7} L${x + w / 2},${feetY} Z`}
              fill="var(--color-ink, black)"
              opacity={0.7}
            />
            <text x={x} y={feetY + 1.6} textAnchor="middle" fontSize={1.3} fill="var(--color-ink, black)" opacity={0.6}>
              {`${Math.round(scaleAt(row, depth) * 100)}%`}
            </text>
          </g>
        )
      })}

      <line
        data-horizon="1"
        x1={0}
        y1={y}
        x2={VIEW_W}
        y2={y}
        stroke="var(--color-warn)"
        strokeWidth={active ? 0.5 : 0.32}
        strokeDasharray="2 1.2"
        vectorEffect="non-scaling-stroke"
        style={{ cursor: 'ns-resize' }}
        onPointerDown={onPointerDown}
      />
      <rect
        data-horizon="1"
        x={0}
        y={y - 0.9}
        width={VIEW_W}
        height={1.8}
        fill="transparent"
        style={{ cursor: 'ns-resize' }}
        onPointerDown={onPointerDown}
      >
        <title>Where the ground meets the sky — drag until the characters look the right size</title>
      </rect>
    </g>
  )
})
